import Box from '@mui/material/Box';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { addBookmark, openBookmarkLoginRequiredModal, removeBookmark } from '../../../ducks/bookmarks.duck';
import { petrol, yellow } from '../../../styles/mui/colors';
import useAppSelector from '../../../util/hooks/useAppSelector';
import IconHeart from '../../Icons/IconHeart';

const bookmarkBoxStyle = {
  fontSize: '0',
  lineHeight: '0',
  transition: 'all 0.2s ease-in-out',
  transform: 'scale(1)',
  '&:hover': {
    cursor: 'pointer',
    transform: 'scale(1.1)',
  },
  backgroundColor: 'white',
  borderRadius: 1,
  p: 0.5,
};

/**
 * BookmarkComponent - Toggles bookmark status for a listing.
 *
 * Props:
 * - listingId (object): Unique identifier with `uuid` for the listing. Required.
 *
 * Redux Selectors:
 * - isAuthenticated (boolean): User's login status.
 * - currentBookmarks (Array): List of the user's bookmarked listings by UUID.
 *
 * Functionality:
 * - Initializes `bookmarked` state based on `currentBookmarks`.
 * - handleBookmarkToggle: Adds/removes bookmark based on current status.
 *   - Opens login modal if user is not authenticated.
 *
 * Renders:
 * - A clickable heart icon that changes color to indicate bookmark status.
 */
const BookmarkComponent = ({ listingId }) => {
  const [bookmarked, setBookmarked] = useState(false);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const currentBookmarks = useAppSelector((state) => state.bookmarks.currentBookmarks);
  const dispatch = useDispatch();

  useEffect(() => {
    setBookmarked(currentBookmarks.includes(listingId.uuid));
  }, [currentBookmarks, listingId.uuid]);

  const handleBookmarkToggle = useCallback(() => {
    if (!isAuthenticated) {
      dispatch(openBookmarkLoginRequiredModal());
      return;
    }

    if (bookmarked) {
      setBookmarked(false);
      dispatch(removeBookmark(listingId.uuid)).catch(() => setBookmarked(true));
    } else {
      setBookmarked(true);
      dispatch(addBookmark(listingId.uuid)).catch(() => setBookmarked(false));
    }
  }, [isAuthenticated, bookmarked, dispatch, listingId.uuid]);

  return (
    <Box onClick={handleBookmarkToggle} sx={bookmarkBoxStyle}>
      <IconHeart
        color="#fff"
        fill={bookmarked ? yellow.main : '#fff'}
        sx={{
          transition: 'all 0.2s ease-in-out',
          fontSize: '18px',
          color: bookmarked ? yellow.main : petrol.main,
        }}
      />
    </Box>
  );
};

BookmarkComponent.displayName = 'Bookmark';

export default memo(BookmarkComponent);
